import React, { useRef, useState, useEffect } from 'react';
import { Progress } from "../ui/progress";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
} from "../ui/dialog";
import { 
  UploadCloud, 
  Smartphone, 
  Subtitles, 
  Crop, 
  Scissors, 
  Play, 
  X, 
  CheckCircle2, 
  AlertTriangle,
  FileVideo,
  FileImage,
  Loader2,
  Library,
  Sparkles,
  Upload,
  Gamepad2,
  Tag,
  Info
} from 'lucide-react';
import axios from 'axios';
import VideoTrimmer from '../VideoTrimmer';
import { useGlobalState } from '../../contexts/GlobalStateContext';
import { useAppConfig } from '../../hooks/useAppConfig';
import VideoCaptionManager, { VideoCaption } from '../VideoCaptionsManager';
import VideoTrimmerEditor from '../VideoTrimmerEditor';
import MediaGallerySelector from '../MediaGallerySelector';
import { cn } from "../../lib/utils";

// shadcn UI
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Separator } from "../ui/separator";
import { toast } from "sonner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

import { API_BASE_URL } from '../../config';

interface PostMedia {
  id: string;
  filePath: string;
  thumbnailPath?: string;
  mediaType: string;
  createdAt: string;
  post?: {
    id: string;
    game: {
      name: string;
    };
  };
}

interface MediaPreviewProps {
  mediaPath: string;
  mediaPreview: string;
  onMediaPathChange: (path: string) => void;
  onMediaPreviewChange: (preview: string) => void;
  onError: (error: string) => void;
  onWarning?: (warning: string) => void;
  onVideoStartTimeChange?: (startTime: number) => void;
  onVideoEndTimeChange?: (endTime: number) => void;
  videoStartTime?: number | null;
  videoEndTime?: number | null;
  onFileChange?: (file: File | null) => void;
  onDeleteVideo?: () => void;
  onVideoDurationChange?: (duration: number) => void;
  needsMobileFormat?: boolean;
  cropData?: { x: number; y: number; width: number; height: number; aspect: number } | null;
  onOpenCropper?: () => void;
  uploading?: boolean;
  generatingCaptions?: boolean;
  onVideoDimensionsChange?: (dimensions: { width: number; height: number }) => void;
  onCaptionsChange?: (captions: VideoCaption[]) => void;
  initialCaptions?: VideoCaption[];
  crop?: { x: number; y: number } | null;
  zoom?: number | null;
  localFile?: File | null;
  onRecommendVideo?: () => void;
  recommendingVideo?: boolean;
  onCategorySelect?: (category: string) => void;
  selectedCategory?: string | null;
  onCropChange?: (cropData: any, crop: { x: number; y: number } | null, zoom: number) => void;
  unifyFormat?: boolean;
  onUnifyFormatChange?: (unify: boolean) => void;
  showUnifyOption?: boolean;
  maxDuration?: number;
}

export default function MediaPreview({
  mediaPath,
  mediaPreview,
  onMediaPathChange,
  onMediaPreviewChange,
  onError,
  onWarning,
  onVideoStartTimeChange,
  onVideoEndTimeChange,
  videoStartTime,
  videoEndTime,
  onFileChange,
  onDeleteVideo,
  onVideoDurationChange,
  needsMobileFormat = false,
  cropData = null,
  onOpenCropper,
  uploading = false,
  generatingCaptions = false,
  onVideoDimensionsChange,
  onCaptionsChange,
  initialCaptions = [],
  crop = null,
  zoom = 1,
  localFile: initialLocalFile = null,
  onRecommendVideo,
  recommendingVideo = false,
  onCategorySelect,
  selectedCategory,
  onCropChange,
  unifyFormat = false,
  onUnifyFormatChange,
  showUnifyOption = false,
  maxDuration = 140,
}: MediaPreviewProps): JSX.Element {
  const { localStorage: globalState, updateLocalStorage } = useGlobalState();
  const [, updateAppConfig] = useAppConfig();
  
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [videoReady, setVideoReady] = useState<boolean>(false);
  const [localFileUrl, setLocalFileUrl] = useState<string>('');
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [videoDuration, setVideoDuration] = useState<number>(0);
  const lastMediaPreviewRef = useRef<string>('');
  const [showCaptionsManager, setShowCaptionsManager] = useState<boolean>(false);
  const [showTrimEditor, setShowTrimEditor] = useState<boolean>(false);
  const [captions, setCaptions] = useState<VideoCaption[]>(initialCaptions);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [showGallerySelector, setShowGallerySelector] = useState<boolean>(false);
  const token = localStorage.getItem('token');
  
  const localFile = initialLocalFile;

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handlePaste = (e: ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.indexOf('image') !== -1) {
        const file = item.getAsFile();
        if (file) {
          processFile(file);
          e.preventDefault();
          toast.success("Image pasted from clipboard!");
          break;
        }
      }
    }
  };

  const processFile = async (file: File) => {
    onError('');
    const isVideoFile = file.type.startsWith('video/');
    const TWITTER_SIZE_LIMIT = 1024 * 1024 * 1024;
    
    if (isVideoFile && file.size > TWITTER_SIZE_LIMIT) {
      const sizeGB = file.size / 1024 / 1024 / 1024;
      onWarning?.(`Video size (${sizeGB.toFixed(2)} GB) exceeds X's limit of 1 GB.`);
    }
    
    if (isVideoFile) {
      const localUrl = URL.createObjectURL(file);
      setLocalFileUrl(localUrl);
      onMediaPreviewChange(localUrl);
      onMediaPathChange('local-video');
      if (onFileChange) onFileChange(file);
      
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = async () => {
        setVideoDuration(video.duration);
        if (onVideoDurationChange) onVideoDurationChange(video.duration);
      };
      video.src = localUrl;
    } else if (file.type.startsWith('image/')) {
      onWarning?.('');
      if (onFileChange) onFileChange(file);
      uploadFile(file);
    } else {
      toast.error("Unsupported file type. Please upload a video or image.");
    }
  };

  // Add paste event listener
  useEffect(() => {
    const handlePasteEvent = (e: Event) => handlePaste(e as ClipboardEvent);
    window.addEventListener('paste', handlePasteEvent);
    return () => {
      window.removeEventListener('paste', handlePasteEvent);
    };
  }, []);

  useEffect(() => {
    // Only generate object URL if it's a real Blob/File and we don't have a valid local URL yet
    // Or if the current mediaPreview is a blob URL that might have been revoked
    const isRevokedBlob = mediaPreview?.startsWith('blob:') && !localFileUrl;
    
    if (initialLocalFile instanceof Blob && (!localFileUrl || isRevokedBlob)) {
      const url = URL.createObjectURL(initialLocalFile);
      setLocalFileUrl(url);
      onMediaPreviewChange(url);
    }
  }, [initialLocalFile, mediaPreview]);
  
  const addEndLogo = globalState.addEndLogo;
  const setAddEndLogo = (value: boolean) => updateLocalStorage({ addEndLogo: value });
  
  const isVideo = (localFile instanceof Blob)
    ? localFile?.type?.startsWith('video/')
    : (mediaPath?.toLowerCase().match(/\.(mp4|mov|avi|mkv|webm|m4v|webm)/)
    || mediaPreview?.toLowerCase().match(/\.(mp4|mov|avi|mkv|webm|m4v|webm)/)
    || mediaPreview?.toLowerCase().includes('path=media%2f') // Detect media path in our API URL
    || mediaPreview?.toLowerCase().includes('/media?path=') 
    || mediaPath === 'local-video'
    || mediaPath?.toLowerCase().endsWith('.mp4')
    || mediaPath?.toLowerCase().endsWith('.mov')
    || mediaPath?.toLowerCase().endsWith('.webm'));

  const isImage = (localFile instanceof Blob)
    ? localFile?.type?.startsWith('image/')
    : (mediaPath?.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)/)
    || mediaPreview?.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)/)
    || mediaPath?.toLowerCase().endsWith('.jpg')
    || mediaPath?.toLowerCase().endsWith('.jpeg')
    || mediaPath?.toLowerCase().endsWith('.png')
    || mediaPath?.toLowerCase().endsWith('.gif')
    || mediaPath?.toLowerCase().endsWith('.webp'));

  useEffect(() => {
    // Only load metadata if we have a valid video preview URL
    if (!isVideo || !mediaPreview || typeof mediaPreview !== 'string') return;
    
    // Avoid re-analyzing if it's the same URL and we already have duration
    if (lastMediaPreviewRef.current === mediaPreview && videoDuration > 0) return;

    console.log('[MediaPreview] Analyzing video metadata for:', mediaPreview.substring(0, 30) + '...');
    setVideoDuration(0);
    setVideoReady(false);

    let isCancelled = false;
    const tempVideo = document.createElement('video');
    tempVideo.preload = 'metadata';
    tempVideo.src = mediaPreview;
    
    const handleLoadedMetadata = () => {
      if (isCancelled) return;
      console.log('[MediaPreview] Metadata loaded. Duration:', tempVideo.duration);
      if (tempVideo.duration && !isNaN(tempVideo.duration) && tempVideo.duration > 0) {
        lastMediaPreviewRef.current = mediaPreview;
        setVideoDuration(tempVideo.duration);
        if (onVideoDurationChange) onVideoDurationChange(tempVideo.duration);
        setVideoReady(true);
      }
    };
    
    const handleError = () => {
      if (isCancelled) return;
      console.error('Error loading video metadata from URL');
    };
    
    tempVideo.addEventListener('loadedmetadata', handleLoadedMetadata);
    tempVideo.addEventListener('error', handleError);
    
    return () => {
      isCancelled = true;
      tempVideo.removeEventListener('loadedmetadata', handleLoadedMetadata);
      tempVideo.removeEventListener('error', handleError);
    };
  }, [mediaPreview, isVideo, onVideoDurationChange, videoDuration]);

  useEffect(() => {
    return () => {
      if (localFileUrl) URL.revokeObjectURL(localFileUrl);
    };
  }, [localFileUrl]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleSelectFileClick = () => {
    fileInputRef.current?.click();
  };

  const uploadFile = async (file: File) => {
    setUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append('media', file);
      const response = await axios.post(`${API_BASE_URL}/social/upload-file`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        }
      });
      const serverPath = response.data.filePath;
      onMediaPathChange(serverPath);
      if (!file.type.startsWith('video/')) {
        onMediaPreviewChange(`${API_BASE_URL}/social/media?path=${encodeURIComponent(serverPath)}&token=${token}`);
      }
      setUploadProgress(null);
    } catch (err: any) {
      toast.error(err.message || 'Failed to upload file');
      setUploadProgress(null);
      if (localFileUrl) {
        URL.revokeObjectURL(localFileUrl);
        setLocalFileUrl('');
      }
      onMediaPreviewChange('');
      onMediaPathChange('');
    }
  };

  const handleDelete = () => {
    if (localFileUrl) URL.revokeObjectURL(localFileUrl);
    setLocalFileUrl('');
    setVideoDuration(0);
    setCaptions([]);
    onFileChange?.(null);
    onVideoStartTimeChange?.(0);
    onVideoEndTimeChange?.(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onDeleteVideo?.();
  };

  const handleCaptionsChange = (newCaptions: VideoCaption[]) => {
    setCaptions(newCaptions);
    onCaptionsChange?.(newCaptions);
  };

  const handleGallerySelect = (selected: PostMedia) => {
    onMediaPathChange(selected.filePath);
    // Use the main file path for the preview so the video player/image can load it
    onMediaPreviewChange(`${API_BASE_URL}/social/media?path=${encodeURIComponent(selected.filePath)}&token=${token}`);
    
    // Since it's from gallery, it's not a local file
    onFileChange?.(null);
    if (localFileUrl) URL.revokeObjectURL(localFileUrl);
    setLocalFileUrl('');
  };

  return (
    <div 
      className={cn(
        "bg-card border rounded-3xl overflow-hidden flex flex-col h-full shadow-2xl transition-all duration-300",
        isDragging ? "border-primary bg-primary/5 scale-[1.01] ring-4 ring-primary/10" : "border-border"
      )}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <MediaGallerySelector 
        open={showGallerySelector}
        onClose={() => setShowGallerySelector(false)}
        onSelect={handleGallerySelect}
      />
      {mediaPath && isVideo && (
        <div className="p-4 bg-muted/50 border-b border-border flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-6 overflow-x-auto pb-2 md:pb-0 scrollbar-hide">
            {/* <div className="flex items-center space-x-2 shrink-0">
              <Checkbox id="logo" checked={addEndLogo} onCheckedChange={setAddEndLogo} className="border-border data-[state=checked]:bg-primary" />
              <Label htmlFor="logo" className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider cursor-pointer">Add Logo</Label>
            </div> */}
            {showUnifyOption && (
              <div className="flex items-center space-x-2 shrink-0">
                <Checkbox id="unify" checked={!unifyFormat} onCheckedChange={(v: boolean) => onUnifyFormatChange?.(!v)} className="border-border data-[state=checked]:bg-primary" />
                <div className="flex items-center gap-1.5">
                  <Label htmlFor="unify" className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider cursor-pointer">Create vertical version</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="bg-muted border-border text-[10px] text-foreground max-w-[200px]">
                        Enable a separate format specifically for mobile platforms (TikTok, Instagram, etc.)
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            )}
          </div>

          <Separator orientation="vertical" className="h-6 bg-card hidden md:block" />

          <div className="flex items-center gap-2 ml-auto">
            <div className="flex items-center bg-background rounded-full border border-border p-0.5 ml-2">
              {onOpenCropper && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onOpenCropper}
                  disabled={uploading || generatingCaptions}
                  className={cn(
                    "h-7 rounded-full text-[10px] font-black px-3",
                    cropData ? "text-green-400 bg-green-400/10" : "text-primary hover:text-foreground"
                  )}
                >
                  <Crop className="h-3 w-3 mr-1.5" />
                  {cropData ? 'CROPPED' : 'CROP'}
                </Button>
              )}
              {videoDuration > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowTrimEditor(true)}
                  disabled={uploading || generatingCaptions}
                  className="h-7 rounded-full text-[10px] font-black px-3 text-primary hover:text-foreground"
                >
                  <Scissors className="h-3 w-3 mr-1.5" />
                  TRIM
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowCaptionsManager(true)}
                disabled={uploading || generatingCaptions || videoDuration === 0}
                className={cn(
                  "h-7 rounded-full text-[10px] font-black px-3",
                  captions.length > 0 ? "text-green-400 bg-green-400/10" : "text-primary hover:text-foreground"
                )}
              >
                <Subtitles className="h-3 w-3 mr-1.5" />
                {captions.length > 0 ? `EDIT (${captions.length})` : 'EDIT'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col min-h-0">
        {(mediaPath || localFile) ? (
          <div className="flex-1 flex flex-col p-4">
            {isVideo ? (
              <div className="flex-1 flex flex-col min-h-0 bg-black rounded-2xl overflow-hidden border border-border shadow-inner relative group">
                {videoDuration > 0 && (mediaPreview || localFile) ? (
                    <VideoTrimmer
                      videoUrl={mediaPreview || (localFile ? URL.createObjectURL(localFile) : '')}
                      videoDuration={videoDuration}
                      onStartTimeChange={(s) => onVideoStartTimeChange?.(s)}
                      onEndTimeChange={(e) => onVideoEndTimeChange?.(e)}
                      maxDuration={maxDuration}
                      addEndLogo={addEndLogo}
                      mediaPath={localFile ? undefined : mediaPath}
                      onVideoDimensionsChange={onVideoDimensionsChange}
                      convertTo60FPS={true}
                      localFile={localFile}
                      onDelete={handleDelete}
                      needsMobileFormat={needsMobileFormat}
                      cropData={cropData}
                      onCaptionsChange={handleCaptionsChange}
                      initialCaptions={captions}
                      openCaptionsManager={showCaptionsManager}
                      onCaptionsManagerClose={() => setShowCaptionsManager(false)}
                      initialStartTime={videoStartTime ?? 0}
                      initialEndTime={videoEndTime ?? videoDuration}
                      crop={crop}
                      zoom={zoom}
                      onCropChange={onCropChange}
                    />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center space-y-6 p-8">
                    <div className="flex flex-col items-center space-y-4">
                      <Loader2 className="h-10 w-10 animate-spin text-primary" />
                      <p className="text-muted-foreground text-xs font-black uppercase tracking-widest animate-pulse">
                        Analyzing Media...
                      </p>
                    </div>

                    {/* Category Selector during analysis/upload */}
                    <div className="w-full max-w-xs space-y-4 bg-muted/50 p-6 rounded-[2rem] border border-border animate-in fade-in slide-in-from-bottom-4 duration-700 delay-300">
                      <div className="flex items-center gap-2 justify-center">
                        <Tag className="h-3 w-3 text-primary" />
                        <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground text-center">Classify this video</span>
                      </div>
                      <div className="grid grid-cols-1 gap-2">
                        {[
                          { id: 'gameplay', label: 'Gameplay', icon: Gamepad2 },
                          { id: 'behind', label: 'New Feature', icon: Sparkles },
                          { id: 'trailer', label: 'Trailer', icon: FileVideo }
                        ].map((cat) => (
                          <button
                            key={cat.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              onCategorySelect?.(cat.id);
                            }}
                            className={cn(
                              "flex items-center p-3 rounded-xl border-2 transition-all gap-3",
                              selectedCategory === cat.id
                                ? "bg-primary/20 border-primary text-primary-foreground"
                                : "bg-background/40 border-border text-muted-foreground hover:border-border hover:text-muted-foreground"
                            )}
                          >
                            <cat.icon className={cn("h-4 w-4", selectedCategory === cat.id ? "text-primary" : "text-muted-foreground")} />
                            <span className="text-[10px] font-bold uppercase tracking-tight">{cat.label}</span>
                            {selectedCategory === cat.id && <div className="ml-auto h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_8px_hsl(var(--primary)/0.8)]" />}
                          </button>
                        ))}
                      </div>
                      <p className="text-[8px] text-muted-foreground italic text-center leading-tight">
                        This helps our AI prioritize this video for relevant missions.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : isImage ? (
              <div className="flex-1 bg-black rounded-2xl overflow-hidden border border-border shadow-inner flex items-center justify-center relative group">
                <img src={mediaPreview} alt="" className="max-w-full max-h-full object-contain" />
                <Button 
                  variant="destructive" 
                  size="icon" 
                  onClick={handleDelete}
                  className="absolute top-4 right-4 h-8 w-8 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center bg-muted rounded-2xl border-2 border-dashed border-border">
                <div className="text-center space-y-2">
                  <div className="bg-card p-4 rounded-full w-fit mx-auto">
                    <FileVideo className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <p className="text-muted-foreground text-xs font-bold uppercase truncate max-w-[200px]">
                    {mediaPath.split('/').pop()}
                  </p>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center p-10 text-center space-y-8 animate-in fade-in duration-500">
            <div className="relative group">
              <div className="absolute -inset-4 bg-primary/10 rounded-full blur-2xl group-hover:bg-primary/20 transition-all duration-500" />
              <div className="relative bg-background p-8 rounded-[40px] border border-border shadow-2xl group-hover:border-primary/30 transition-all duration-500 transform group-hover:scale-105 group-active:scale-95">
                <UploadCloud className="h-16 w-16 text-primary mb-2 mx-auto" strokeWidth={1.5} />
                <div className="space-y-1">
                  <h4 className="text-xl font-black text-foreground tracking-tight">Post Content</h4>
                  <p className="text-muted-foreground text-xs font-medium">Drag & drop, paste or browse</p>
                </div>
              </div>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            
            <div className="flex flex-col gap-3">
              <Button
                onClick={handleSelectFileClick}
                className="bg-primary hover:bg-primary/90 text-white font-black h-14 px-8 rounded-2xl shadow-lg shadow-primary/20 active:scale-[0.98] transition-all text-lg tracking-tight"
              >
                SELECT MEDIA
              </Button>
              
              <div className="flex items-center">
                <div className="flex-grow border-t border-border"></div>
                <span className="mx-4 text-[10px] font-black text-muted-foreground uppercase tracking-widest">OR</span>
                <div className="flex-grow border-t border-border"></div>
              </div>

              <Button
                variant="outline"
                onClick={() => setShowGallerySelector(true)}
                className="border-border hover:bg-card text-foreground font-bold h-12 rounded-xl transition-all"
              >
                <Library className="h-4 w-4 mr-2" />
                FROM GALLERY
              </Button>

              {onRecommendVideo && (
                <Button
                  variant="ghost"
                  onClick={onRecommendVideo}
                  disabled={recommendingVideo}
                  className="text-primary hover:text-primary/80 hover:bg-primary/10 font-bold h-10 rounded-xl transition-all"
                >
                  {recommendingVideo ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4 mr-2" />
                  )}
                  RECOMMEND VIDEO
                </Button>
              )}

              <div className="flex items-center justify-center gap-4 text-muted-foreground mt-2">
                <div className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest">
                  <FileVideo className="h-3 w-3" /> Video
                </div>
                <div className="h-1 w-1 rounded-full bg-card" />
                <div className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest">
                  <FileImage className="h-3 w-3" /> Image
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {showTrimEditor && videoDuration > 0 && mediaPreview && onVideoStartTimeChange && onVideoEndTimeChange && (
        <VideoTrimmerEditor
          open={showTrimEditor}
          onClose={() => setShowTrimEditor(false)}
          onTrimComplete={(s, e) => {
            onVideoStartTimeChange(s);
            onVideoEndTimeChange(e);
          }}
          videoUrl={mediaPreview}
          videoDuration={videoDuration}
          initialStartTime={videoStartTime ?? 0}
          initialEndTime={videoEndTime ?? videoDuration}
          needsMobileFormat={needsMobileFormat}
        />
      )}

      {/* Upload Progress Dialog */}
      <Dialog open={uploadProgress !== null} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-[425px] bg-card border-border text-foreground p-0 overflow-hidden">
          <div className="p-8 space-y-6">
            <div className="flex items-center gap-4">
              <div className="bg-primary/10 p-3 rounded-2xl border border-primary/20">
                <Upload className="h-6 w-6 text-primary animate-bounce" />
              </div>
              <div>
                <DialogTitle className="text-xl font-black text-foreground tracking-tight italic uppercase">Uploading Asset</DialogTitle>
                <p className="text-xs text-muted-foreground font-medium">Please wait while we process your file.</p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                <span>Transfer Status</span>
                <span className="text-primary">{uploadProgress}%</span>
              </div>
              <Progress value={uploadProgress || 0} className="h-3 bg-muted [&>div]:bg-primary" />
            </div>

            <div className="bg-muted/50 rounded-xl p-4 border border-border/50">
              <div className="flex items-center gap-2">
                <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                <span className="text-[9px] font-black uppercase tracking-[0.2em] text-muted-foreground">
                  {uploadProgress === 100 ? 'Encrypting & Verifying' : 'Transferring Data'}
                </span>
              </div>
            </div>

            {/* Category Selector while uploading */}
            {isVideo && (
              <div className="space-y-3 pt-2">
                <div className="flex items-center gap-2">
                  <Tag className="h-3 w-3 text-primary" />
                  <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Classify this video</span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: 'gameplay', label: 'Gameplay', icon: Gamepad2 },
                    { id: 'behind', label: 'New Feature', icon: Sparkles },
                    { id: 'trailer', label: 'Trailer', icon: FileVideo }
                  ].map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => onCategorySelect?.(cat.id)}
                      className={cn(
                        "flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all gap-1.5",
                        selectedCategory === cat.id
                          ? "bg-primary/20 border-primary text-primary-foreground"
                          : "bg-muted/40 border-border text-muted-foreground hover:border-border hover:text-muted-foreground"
                      )}
                    >
                      <cat.icon className={cn("h-4 w-4", selectedCategory === cat.id ? "text-primary" : "text-muted-foreground")} />
                      <span className="text-[9px] font-bold uppercase tracking-tight">{cat.label}</span>
                    </button>
                  ))}
                </div>
                <p className="text-[8px] text-muted-foreground italic text-center">
                  This helps our AI better understand your content for future missions.
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
